import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  PlusIcon, XIcon, PrinterIcon, ArrowsLeftRightIcon, BankIcon, ReceiptIcon, VaultIcon,
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

export default function Deposits() {
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [clients, setClients] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [openPrint, setOpenPrint] = useState(null);
  const [openCash, setOpenCash] = useState(false);

  const voidDep = async (d) => {
    if (!window.confirm(`¿Anular depósito ${d.numero}? Se revierten tesorería, asiento y abono a factura.`)) return;
    try { await api.delete(`/treasury/deposits/${d.id}`); toast.success("Depósito anulado"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const load = async () => {
    const [d, b, c] = await Promise.all([
      api.get("/treasury/deposits"),
      api.get("/treasury/accounts"),
      api.get("/clients"),
    ]);
    setItems(d.data); setBanks(b.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const total = items.reduce((s, x) => s + (x.monto || 0), 0);

  return (
    <Layout
      title="Depósitos Bancarios"
      subtitle={`PRD 4.4 · ${items.length} depósitos · Total ${fmtL(total)}`}
      actions={
        <div className="flex gap-2">
        <button onClick={() => setOpenCash(true)} data-testid="cash-deposit-btn"
          className="px-3 py-2 text-sm border border-border rounded-md text-text-secondary hover:text-text-primary hover:border-agro-yellow/40">
          Depositar efectivo
        </button>
        <button
          onClick={() => setOpenForm(true)}
          data-testid="new-deposit-btn"
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2"
        >
          <PlusIcon size={16} weight="bold" /> Nuevo depósito
        </button>
        </div>
      }
    >
      {items.length === 0 ? (
        <div className="bg-card border border-border/60 rounded-lg p-10 text-center">
          <VaultIcon size={32} className="text-text-muted mx-auto mb-3" weight="duotone" />
          <div className="text-text-secondary text-sm">Sin depósitos registrados</div>
          <div className="text-text-muted text-xs mt-1">Crea uno usando el botón "Nuevo depósito"</div>
        </div>
      ) : (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">N°</th>
                <th className="p-3">Fecha</th>
                <th className="p-3">Origen</th>
                <th className="p-3"></th>
                <th className="p-3">Destino</th>
                <th className="p-3">De cliente / Descripción</th>
                <th className="p-3 text-right">Monto</th>
                <th className="p-3 pr-5 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-t border-border/30 hover:bg-white/[0.02]" data-testid={`deposit-row-${d.numero}`}>
                  <td className="p-3 pl-5 font-mono text-xs text-agro-green">{d.numero}</td>
                  <td className="p-3 text-xs">{fmtDate(d.fecha)}</td>
                  <td className="p-3 text-xs">
                    {d.cuenta_origen_codigo ? (
                      <span className="font-mono text-text-secondary">{d.cuenta_origen_codigo}</span>
                    ) : (
                      <span className="text-text-muted italic">Externo</span>
                    )}
                  </td>
                  <td className="p-3 text-text-muted"><ArrowsLeftRightIcon size={14} /></td>
                  <td className="p-3 text-xs font-mono text-agro-green">{d.cuenta_destino_codigo}</td>
                  <td className="p-3 text-sm">
                    {d.cliente_nombre && <div className="text-text-primary">{d.cliente_nombre}{d.factura_numero ? ` · Fact. ${d.factura_numero}` : ""}</div>}
                    {d.descripcion && <div className="text-xs text-text-muted">{d.descripcion}</div>}
                    {d.referencia_bancaria && <div className="text-[10px] text-text-muted font-mono">Ref: {d.referencia_bancaria}</div>}
                  </td>
                  <td className="p-3 text-right text-sm font-medium text-agro-green">{fmtL(d.monto)}</td>
                  <td className="p-3 pr-5 text-right">
                    <button
                      onClick={() => setOpenPrint(d)}
                      data-testid={`print-deposit-${d.numero}`}
                      className="text-agro-yellow hover:underline text-xs flex items-center gap-1"
                    >
                      <PrinterIcon size={12} /> Imprimir
                    </button>
                    <button onClick={() => voidDep(d)} data-testid={`void-deposit-${d.numero}`} className="text-red-400 hover:underline text-xs mt-1">Anular</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openForm && (
        <DepositForm
          banks={banks} clients={clients}
          onClose={() => setOpenForm(false)}
          onSaved={(created) => { setOpenForm(false); load(); if (created) setOpenPrint(created); }}
        />
      )}
      {openPrint && <PrintVoucher dep={openPrint} onClose={() => setOpenPrint(null)} />}
      {openCash && <CashDepositModal banks={banks} onClose={() => setOpenCash(false)}
        onSaved={(created) => { setOpenCash(false); load(); if (created) setOpenPrint(created); }} />}
    </Layout>
  );
}

function DepositForm({ banks, clients, onClose, onSaved }) {
  const [form, setForm] = useState({
    cuenta_origen_id: "", cuenta_destino_id: "", monto: 0,
    descripcion: "", cliente_id: "", referencia_bancaria: "",
    realizado_por: "", fecha: "", factura_id: "", origen_tipo: "auto",
  });
  const [facturas, setFacturas] = useState([]);
  useEffect(() => {
    if (!form.cliente_id || form.cuenta_origen_id) { setFacturas([]); return; }
    api.get("/invoices", { params: { cliente_id: form.cliente_id } })
      .then((r) => setFacturas((r.data || []).filter((i) => i.cliente_id === form.cliente_id && i.estado !== "anulada" && (i.saldo_pendiente || 0) > 0)))
      .catch(() => setFacturas([]));
  }, [form.cliente_id, form.cuenta_origen_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cuenta_destino_id) return toast.error("Cuenta destino requerida");
    if (Number(form.monto) <= 0) return toast.error("Monto debe ser > 0");
    if (form.cuenta_origen_id === form.cuenta_destino_id) {
      return toast.error("Origen y destino no pueden ser la misma cuenta");
    }
    try {
      const { data } = await api.post("/treasury/deposits", {
        cuenta_origen_id: form.cuenta_origen_id || null,
        cuenta_destino_id: form.cuenta_destino_id,
        monto: Number(form.monto),
        descripcion: form.descripcion,
        cliente_id: form.cliente_id || null,
        factura_id: !form.cuenta_origen_id && form.factura_id ? form.factura_id : null,
        origen_tipo: form.origen_tipo,
        referencia_bancaria: form.referencia_bancaria,
        realizado_por: form.realizado_por,
        fecha: form.fecha ? new Date(form.fecha).toISOString() : undefined,
      });
      toast.success(`Depósito ${data.numero} registrado`);
      onSaved(data);
    } catch (er) { toast.error(apiErr(er)); }
  };

  const cajaAccounts = banks.filter((b) => b.tipo === "caja");
  const bankAccounts = banks.filter((b) => b.tipo === "banco");

  const m = useModalForm({ value: form, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="deposit-modal">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">PRD 4.4 · Depósito</div>
            <h3 className="font-heading text-lg font-semibold mt-0.5">Nuevo depósito</h3>
          </div>
          <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>
        <form onSubmit={m.guardar} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Cuenta origen (caja o externo)</label>
              <select
                data-testid="dep-origen"
                value={form.cuenta_origen_id}
                onChange={(e) => setForm({ ...form, cuenta_origen_id: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="">— Externo / sin origen interno —</option>
                {cajaAccounts.map((b) => <option key={b.id} value={b.id}>{b.codigo} · {b.nombre} ({fmtL(b.saldo_actual || 0)})</option>)}
                {bankAccounts.map((b) => <option key={b.id} value={b.id}>{b.codigo} · {b.nombre} ({fmtL(b.saldo_actual || 0)})</option>)}
              </select>
              <p className="text-[10px] text-text-muted mt-1">Si dejas vacío, será un depósito externo (ingreso de fondos).</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Cuenta destino (banco) *</label>
              <select
                data-testid="dep-destino"
                value={form.cuenta_destino_id}
                onChange={(e) => setForm({ ...form, cuenta_destino_id: e.target.value })}
                required
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="">— Seleccionar banco —</option>
                {bankAccounts.map((b) => <option key={b.id} value={b.id}>{b.codigo} · {b.nombre} ({fmtL(b.saldo_actual || 0)})</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Monto (L.) *</label>
              <input
                data-testid="dep-monto"
                type="number" step="0.01" min="0"
                value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })}
                required
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Fecha</label>
              <input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">De cliente (opcional)</label>
            <select
              value={form.cliente_id}
              onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
            >
              <option value="">— Sin cliente asociado —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)}
            </select>
          </div>
          {!form.cuenta_origen_id && form.cliente_id && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Aplicar a factura del cliente</label>
              <select value={form.factura_id} onChange={(e) => setForm({ ...form, factura_id: e.target.value })} data-testid="dep-factura"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                <option value="">— Sin factura (queda como anticipo del cliente) —</option>
                {facturas.map((f) => <option key={f.id} value={f.id}>{f.numero} · saldo {fmtL(f.saldo_pendiente)}</option>)}
              </select>
              <p className="text-[10px] text-text-muted mt-1">Asiento: Debe banco / Haber CxC del cliente (reduce el saldo de la factura).</p>
            </div>
          )}
          {!form.cuenta_origen_id && !form.cliente_id && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Origen del dinero</label>
              <select value={form.origen_tipo} onChange={(e) => setForm({ ...form, origen_tipo: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                <option value="auto">Otro ingreso (Haber 4104)</option>
                <option value="caja">Efectivo de caja (Haber 1101)</option>
              </select>
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Descripción / Concepto</label>
            <input
              data-testid="dep-descripcion"
              value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              placeholder="ej. Depósito día Lunes, cobros del fin de semana"
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Referencia bancaria</label>
              <input
                value={form.referencia_bancaria}
                onChange={(e) => setForm({ ...form, referencia_bancaria: e.target.value })}
                placeholder="N° de boleta / transferencia"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Realizado por</label>
              <input
                value={form.realizado_por}
                onChange={(e) => setForm({ ...form, realizado_por: e.target.value })}
                placeholder="Nombre del responsable"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={m.cerrar} className="flex-1 bg-app border border-border hover:bg-white/[0.03] text-text-primary rounded-md py-2.5 text-sm">Cancelar</button>
            <button disabled={m.saving}
              type="submit"
              data-testid="save-deposit-btn"
              className="flex-1 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-2.5 text-sm"
            >
              Registrar y abrir comprobante
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PrintVoucher({ dep, onClose }) {
  const handlePrint = () => window.print();
  const m = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 print:bg-white print:p-0" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-xl max-h-[92vh] overflow-y-auto print:bg-white print:border-0 print:max-h-none print:max-w-none print:rounded-none" onClick={(e) => e.stopPropagation()} data-testid="deposit-voucher-modal">
        <div className="p-4 border-b border-border flex items-center justify-between print:hidden">
          <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted">Comprobante de Depósito</h3>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint} data-testid="print-voucher-btn" className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-3 py-1.5 text-xs flex items-center gap-1">
              <PrinterIcon size={12} weight="bold" /> Imprimir
            </button>
            <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={18} /></button>
          </div>
        </div>
        <div className="p-6 voucher-printable bg-card print:bg-white print:text-black">
          <div className="text-center pb-4 border-b-2 border-agro-green print:border-black mb-5">
            <div className="font-heading text-2xl font-bold text-agro-green print:text-black">AGROPORT</div>
            <div className="text-xs text-text-muted print:text-gray-700">ERP · Honduras</div>
            <div className="font-heading text-lg mt-3 uppercase tracking-wider">Comprobante de Depósito</div>
            <div className="font-mono text-sm text-agro-green print:text-black mt-1">{dep.numero}</div>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Row label="Fecha" value={fmtDate(dep.fecha)} />
            <Row label="Referencia bancaria" value={dep.referencia_bancaria || "—"} />
            <Row label="Cuenta origen" value={dep.cuenta_origen_codigo ? `${dep.cuenta_origen_codigo} · ${dep.cuenta_origen_nombre}` : "Externo"} />
            <Row label="Cuenta destino" value={`${dep.cuenta_destino_codigo} · ${dep.cuenta_destino_nombre}`} />
            {dep.cliente_nombre && <Row label="De cliente" value={dep.cliente_nombre} />}
            <Row label="Realizado por" value={dep.realizado_por || "—"} />
          </div>
          {dep.descripcion && (
            <div className="mt-4 p-3 bg-app print:bg-gray-100 rounded-md text-sm">
              <div className="text-[10px] uppercase tracking-wider text-text-muted print:text-gray-700 mb-1">Concepto</div>
              <div>{dep.descripcion}</div>
            </div>
          )}
          <div className="mt-6 p-4 bg-agro-green/10 border-2 border-agro-green/30 print:bg-gray-50 print:border-black rounded-md flex items-center justify-between">
            <span className="text-sm font-medium">Monto depositado</span>
            <span className="font-heading text-3xl font-semibold text-agro-green print:text-black">{fmtL(dep.monto)}</span>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-12 text-xs text-text-muted print:text-gray-700">
            <div className="text-center">
              <div className="border-t border-border print:border-black pt-2">Entrega</div>
            </div>
            <div className="text-center">
              <div className="border-t border-border print:border-black pt-2">Recibe</div>
            </div>
          </div>
          <div className="mt-6 text-center text-[10px] text-text-muted print:text-gray-500">
            Emitido el {fmtDate(dep.created_at)} · AGROPORT ERP · {dep.created_by}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-muted print:text-gray-600">{label}</div>
      <div className="text-text-primary print:text-black mt-0.5">{value}</div>
    </div>
  );
}

function CashDepositModal({ banks, onClose, onSaved }) {
  const [data, setData] = useState({ movimientos: [], cajas: [] });
  const [sel, setSel] = useState({});
  const [destino, setDestino] = useState("");
  const [ref, setRef] = useState("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  useEffect(() => {
    api.get("/treasury/deposits/cash-pending").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  const chosen = data.movimientos.filter((m) => sel[m.id]);
  const total = chosen.reduce((s, m) => s + (m.monto || 0), 0);
  const cajaSel = chosen.length ? chosen[0].cuenta_id : null;
  const submit = async () => {
    if (!chosen.length) return toast.error("Selecciona cobros en efectivo");
    if (!destino) return toast.error("Selecciona la cuenta bancaria destino");
    try {
      const { data: dep } = await api.post("/treasury/deposits/from-cash", {
        movimiento_ids: chosen.map((m) => m.id), cuenta_destino_id: destino, referencia_bancaria: ref, fecha,
      });
      toast.success(`Depósito ${dep.numero} registrado`);
      onSaved(dep);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const cdm = useModalForm({ value: { sel, destino, ref, fecha }, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...cdm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cash-deposit-modal">
        <div className="p-5 border-b border-border flex justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">Conciliación · Depositar efectivo</div>
            <h3 className="font-heading text-lg font-semibold">Cobros en efectivo pendientes de depositar</h3>
          </div>
          <button aria-label="Cerrar" onClick={cdm.cerrar} className="text-text-muted"><XIcon size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase text-text-muted"><th className="p-2"></th><th className="p-2">Fecha</th><th className="p-2">Caja</th><th className="p-2">Concepto</th><th className="p-2 text-right">Monto</th></tr></thead>
            <tbody>
              {data.movimientos.length === 0 ? <tr><td colSpan="5" className="p-6 text-center text-text-muted">Sin efectivo pendiente</td></tr>
                : data.movimientos.map((m) => (
                  <tr key={m.id} className="border-t border-border/30">
                    <td className="p-2"><input type="checkbox" checked={!!sel[m.id]} disabled={!!cajaSel && cajaSel !== m.cuenta_id}
                      onChange={(e) => setSel({ ...sel, [m.id]: e.target.checked })} className="accent-agro-green" /></td>
                    <td className="p-2 text-xs">{fmtDate(m.fecha)}</td>
                    <td className="p-2 text-xs">{m.cuenta_nombre}</td>
                    <td className="p-2 text-xs">{m.concepto}</td>
                    <td className="p-2 text-right">{fmtL(m.monto)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className="grid grid-cols-3 gap-3">
            <select value={destino} onChange={(e) => setDestino(e.target.value)} className="bg-app border border-border rounded-md px-3 py-2 text-sm">
              <option value="">— Banco destino —</option>
              {banks.filter((b) => b.tipo === "banco").map((b) => <option key={b.id} value={b.id}>{b.codigo} · {b.nombre}</option>)}
            </select>
            <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Ref. boleta bancaria" className="bg-app border border-border rounded-md px-3 py-2 text-sm" />
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="bg-app border border-border rounded-md px-3 py-2 text-sm" />
          </div>
          <button disabled={cdm.saving} onClick={cdm.guardar} className="w-full bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-3" data-testid="cash-deposit-submit">
            Depositar {fmtL(total)} ({chosen.length} cobros)
          </button>
        </div>
      </div>
    </div>
  );
}
